import * as Yup from "yup";
import { Box, Button, Divider, useToast } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ActionPromptContainer,
  ApiMessageStack,
  FormControlV2,
} from "components";
import { FC } from "react";
import { useForm } from "react-hook-form";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import { TSelectOption } from "types/select-input.type";
import { LeadIcon } from "enums/lead-icon.enum";
import { LEAD_ICON_OPTIONS } from "constants/lead-icons";
import { LEAD_TYPE_OPTIONS } from "constants/lead-type-options";
import { LeadIconSelectOption } from "components/select-input/custom-options/LeadIconSelectOption";
import { IGetOptionPropsData } from "components/select-input/SelectInputOptionsController";

type TFieldValues = {
  source: string;
  type: string;
  icon: LeadIcon;
};

const initialValues: TFieldValues = {
  source: "",
  type: "",
  icon: "" as LeadIcon,
};

const validationSchema = Yup.object().shape({
  source: Yup.string().required("Please select a lead source."),
  type: Yup.string().required("Please select a lead type."),
  icon: Yup.string().required("Please select an icon."),
});

interface IProps {
  closePrompt: () => void;
  sourceOptions: TSelectOption<string>[];
  createLeadIcon: (
    source: string,
    type: string,
    icon: LeadIcon
  ) => Promise<void>;
}

export const CreateLeadIconActionPrompt: FC<IProps> = ({
  closePrompt,
  sourceOptions,
  createLeadIcon,
}) => {
  const toast = useToast();

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<TFieldValues>({
    defaultValues: initialValues,
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: TFieldValues) => {
    try {
      await createLeadIcon(data.source, data.type, data.icon);
      toast({
        ...DEFAULT_SUCCESS_TOAST_OPTIONS,
        description: <ApiMessageStack messageStack={"Lead icon saved."} />,
      });
      closePrompt();
    } catch (e) {
      toast({
        ...DEFAULT_ERROR_TOAST_OPTIONS,
        description: <ApiMessageStack messageStack={e.message} />,
      });
    }
  };

  return (
    <ActionPromptContainer
      header={"Add lead icon"}
      body={
        <Box pt={5}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box mb={5}>
              <FormControlV2<TFieldValues>
                name={"source"}
                control={control}
                label={"Lead source"}
                type={FormControlsTypeEnum.SINGLE_SELECT}
                additionalProps={{
                  options: sourceOptions,
                  placeholder: "Select a lead source",
                }}
              />
            </Box>
            <Box mb={5}>
              <FormControlV2<TFieldValues>
                name={"type"}
                control={control}
                label={"Lead type"}
                type={FormControlsTypeEnum.SINGLE_SELECT}
                additionalProps={{
                  options: LEAD_TYPE_OPTIONS,
                  placeholder: "Select a lead type",
                }}
              />
            </Box>
            <Box mb={5}>
              <FormControlV2<TFieldValues>
                name={"icon"}
                control={control}
                label={"Icon"}
                type={FormControlsTypeEnum.SINGLE_SELECT}
                additionalProps={{
                  options: LEAD_ICON_OPTIONS,
                  placeholder: "Select an icon",
                  optionComponent: (
                    optionProps: IGetOptionPropsData<LeadIcon>
                  ) => <LeadIconSelectOption optionProps={optionProps} />,
                }}
              />
            </Box>
            <Divider mb={4} />
            <Box
              width={"100%"}
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
            >
              <Button
                type={"submit"}
                colorScheme={"blue"}
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Save
              </Button>
            </Box>
          </form>
        </Box>
      }
    />
  );
};
